import * as PropTypes from 'prop-types';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    FeatureBlockSection,
    FeatureBlockContent
} from '../FeatureBlockElements';
import HBMediaElement from '../honeybook-ui/HBMediaElement/HBMediaElement';
import HBText from '../honeybook-ui/HBText/HBText';

import './featured-in.scss';

const defaultFeaturedItems = [
    {
        imgUrl: 'marketing_site/home_page/fortune.png',
        alt: 'Fortune'
    },
    {
        imgUrl: 'marketing_site/home_page/cnbc.png',
        alt: 'CNBC'
    },
    {
        imgUrl: 'marketing_site/home_page/fast-company.png',
        alt: 'Fast Company'
    },
    {
        imgUrl: 'marketing_site/home_page/forbes.png',
        alt: 'Forbes'
    },
    {
        imgUrl: 'marketing_site/home_page/reuters.png',
        alt: 'Reuters'
    },
    {
        imgUrl: 'marketing_site/home_page/tech-crunch.png',
        alt: 'Tech Crunch'
    }
];

interface FeaturedInProps {
    featuredItems?: typeof defaultFeaturedItems;
    showTitle?: boolean;
    size?: 'small' | 'large';
    isNewBranding?: boolean;
    staticMode?: boolean;
}

const FeaturedIn = ({
    featuredItems = defaultFeaturedItems,
    showTitle = true,
    size = 'large',
    isNewBranding = false,
    staticMode = false
}: FeaturedInProps) => {
    const { t } = useTranslation();

    const marqueeItems = useMemo(() => {
        if (staticMode) {
            return featuredItems;
        }
        if (size === 'small') {
            return [
                ...featuredItems,
                ...featuredItems,
                ...featuredItems,
                ...featuredItems
            ];
        }
        return [...featuredItems, ...featuredItems];
    }, [featuredItems, staticMode, size]);

    const renderList = (items: typeof featuredItems) => (
        <ul className="featured-in__list">
            {items.map((item, index) => (
                <li key={item.alt + index} className="featured-in__list-item">
                    <HBMediaElement
                        name={item.imgUrl}
                        alt={item.alt}
                        lazyLoad={false}
                    />
                </li>
            ))}
        </ul>
    );

    return (
        <FeatureBlockSection
            lightBackground
            customClass={`featured-in featured-in--${size} ${
                isNewBranding ? 'featured-in--new-branding' : ''
            }`}
        >
            <FeatureBlockContent>
                {showTitle && (
                    <HBText
                        customClass="featured-in__title"
                        tag="h2"
                        type="title-s"
                    >
                        {t('featuredIn')}
                    </HBText>
                )}
                <div className="marquee-container">
                    {staticMode ? (
                        <div className="static-content">
                            {renderList(featuredItems)}
                        </div>
                    ) : (
                        <div className="marquee-content">
                            {renderList(marqueeItems)}
                        </div>
                    )}
                </div>
            </FeatureBlockContent>
        </FeatureBlockSection>
    );
};

FeaturedIn.propTypes = {
    featuredItems: PropTypes.arrayOf(PropTypes.object),
    showTitle: PropTypes.bool,
    size: PropTypes.oneOf(['small', 'large']),
    isNewBranding: PropTypes.bool
};

export default memo(FeaturedIn);
